import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { api } from '../../services/api';
import ImageUploader from '../../components/ImageUploader';
import ProgressBar from '../../components/ProgressBar';
import { Input, TextArea } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import { Card, CardContent } from '../../components/ui/Card';
import { getImageUrl } from '../../utils/imageUtils';

const Container = styled.div`
  max-width: 1000px;
  margin: 0 auto;
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;
  label {
    display: block;
    margin-bottom: 0.5rem;
  }
`;

const PostItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: white;
  border-bottom: 1px solid #eee;
  &:last-child { border-bottom: none; }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const NewsAdmin = () => {
  const [header, setHeader] = useState({ title: '', description: '', image: '' });
  const [posts, setPosts] = useState([]);
  const [view, setView] = useState('list'); // list, editHeader, editPost, createPost
  const [currentPost, setCurrentPost] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [progress, setProgress] = useState({ header: 0, post: 0 });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [headerRes, postsRes] = await Promise.all([api.get('/news/header'), api.get('/news/posts')]);
      setHeader(headerRes);
      setPosts(postsRes);
    } catch (err) {
      console.error(err);
    }
  };

  // Header Handlers
  const handleHeaderSave = async () => {
    setSaving(true);
    try {
      await api.put('/news/header', { title: header.title, description: header.description });
      setMsg('Header saved');
      setTimeout(() => { setMsg(''); setView('list'); }, 2000);
    } catch (err) {
      setMsg('Error saving header');
    } finally {
      setSaving(false);
    }
  };

  const handleHeaderImage = async (file) => {
    if (!file) return;
    setProgress(prev => ({ ...prev, header: 0 }));
    
    const timer = setInterval(() => {
        setProgress(prev => {
            if(prev.header < 90) return { ...prev, header: prev.header + 10 };
            return prev;
        });
    }, 100);

    try {
        const formData = new FormData();
        formData.append('image', file);
        const res = await api.upload('/news/header/image', formData);
        
        clearInterval(timer);
        setProgress(prev => ({ ...prev, header: 100 }));
        
        setHeader(res);
        setTimeout(() => setProgress(prev => ({ ...prev, header: 0 })), 500);
    } catch (err) {
        clearInterval(timer);
        setProgress(prev => ({ ...prev, header: 0 }));
        console.error(err);
    }
  };

  // Post Handlers
  const handleCreatePost = async () => {
    setSaving(true);
    try {
      await api.post('/news/posts', { 
        title: currentPost.title, 
        description: currentPost.description 
      });
      // Upload images if any? (Needs loop)
      setMsg('Post created');
      loadData();
      setView('list');
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdatePost = async () => {
    setSaving(true);
    try {
      await api.put(`/news/posts/${currentPost._id}`, {
        title: currentPost.title,
        description: currentPost.description
      });
      setMsg('Post updated');
      loadData();
      setView('list');
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePost = async (id) => {
    if(!window.confirm('Delete post?')) return;
    try {
      await api.delete(`/news/posts/${id}`);
      loadData();
    } catch (err) {
      console.error(err);
    }
  };

  const handlePostImageUpload = async (files) => {
    if (!files) return;
    const fileList = Array.isArray(files) ? files : [files];
    
    let updatedPost = { ...currentPost };

    for (const file of fileList) {
        setProgress(prev => ({ ...prev, post: 0 }));
        const timer = setInterval(() => {
            setProgress(prev => {
                if(prev.post < 90) return { ...prev, post: prev.post + 10 };
                return prev;
            });
        }, 100);

        const formData = new FormData();
        formData.append('image', file);
        try {
          // Upload each image
          const res = await api.upload(`/news/posts/${updatedPost._id}/image`, formData);
          // res is the updated post object
          updatedPost = res;
          
          clearInterval(timer);
          setProgress(prev => ({ ...prev, post: 100 }));
        } catch (err) {
          clearInterval(timer);
          setProgress(prev => ({ ...prev, post: 0 }));
          console.error(`Upload failed for ${file.name}`, err);
        }
        // Wait a bit before next
        await new Promise(r => setTimeout(r, 200));
    }
    setTimeout(() => setProgress(prev => ({ ...prev, post: 0 })), 500);
    setCurrentPost(updatedPost);
  };

  const handleDeletePostImage = async (index) => {
    if (!window.confirm('Delete image?')) return;
    try {
      const res = await api.delete(`/news/posts/${currentPost._id}/image/${index}`);
      setCurrentPost(res);
    } catch (err) {
      console.error(err);
    }
  };

  const renderList = () => (
    <div>
      <Card style={{marginBottom: '2rem'}}>
        <CardContent>
          <h3>Page Header</h3>
          <p><strong>Title:</strong> {header.title}</p>
          <Button onClick={() => setView('editHeader')} $variant="secondary" style={{marginTop:'1rem'}}>Edit Header</Button>
        </CardContent>
      </Card>
      
      <HeaderRow>
        <h3>Posts</h3>
        <Button onClick={() => { setCurrentPost({ title: '', description: '' }); setView('createPost'); }}>Create New Post</Button>
      </HeaderRow>
      
      <Card>
        {posts.map(post => (
          <PostItem key={post._id}>
            <span>{post.title}</span>
            <ActionButtons>
              <Button onClick={() => { setCurrentPost(post); setView('editPost'); }} $variant="secondary">Edit</Button>
              <Button onClick={() => handleDeletePost(post._id)} $variant="danger">Delete</Button>
            </ActionButtons>
          </PostItem>
        ))}
        {posts.length === 0 && <div style={{padding:'1rem'}}>No posts found.</div>}
      </Card>
    </div>
  );

  const renderEditHeader = () => (
    <Card>
      <CardContent>
        <h3>Edit Header</h3>
        <FormGroup>
          <label>Title</label>
          <Input value={header.title} onChange={e => setHeader({...header, title: e.target.value})} />
        </FormGroup>
        <FormGroup>
          <label>Description</label>
          <TextArea value={header.description} onChange={e => setHeader({...header, description: e.target.value})} />
        </FormGroup>
        <FormGroup>
          <label>Image</label>
          <ImageUploader previewUrl={header.image} onImageSelect={handleHeaderImage} />
          {progress.header > 0 && <ProgressBar progress={progress.header} />}
        </FormGroup>
        <ActionButtons>
            <Button onClick={handleHeaderSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save'}
            </Button>
            <Button onClick={() => setView('list')} $variant="secondary">Cancel</Button>
        </ActionButtons>
      </CardContent>
    </Card>
  );

  const renderPostForm = (isEdit) => (
    <Card>
      <CardContent>
        <h3>{isEdit ? 'Edit Post' : 'Create Post'}</h3>
        <FormGroup>
          <label>Title</label>
          <Input value={currentPost.title} onChange={e => setCurrentPost({...currentPost, title: e.target.value})} />
        </FormGroup>
        <FormGroup>
          <label>Content</label>
          <TextArea value={currentPost.description} onChange={e => setCurrentPost({...currentPost, description: e.target.value})} />
        </FormGroup>
        
        {isEdit && (
            <FormGroup>
                <label>Images</label>
                <ImageUploader onImageSelect={handlePostImageUpload} multiple={true} />
                {progress.post > 0 && <ProgressBar progress={progress.post} />}
                <div style={{display:'flex', gap:'1rem', flexWrap:'wrap', marginTop:'1rem'}}>
                    {currentPost.images && currentPost.images.map((img, idx) => (
                        <div key={idx} style={{position:'relative', width:'100px'}}>
                            <img src={getImageUrl(img)} alt="" style={{width:'100%', borderRadius:'4px'}} />
                            <button 
                                onClick={() => handleDeletePostImage(idx)}
                                style={{position:'absolute', top:0, right:0, background:'red', color:'white', border:'none', cursor:'pointer'}}
                            >x</button>
                        </div>
                    ))}
                </div>
            </FormGroup>
        )}
        
        <ActionButtons>
            <Button onClick={isEdit ? handleUpdatePost : handleCreatePost} disabled={saving}>
                {saving ? 'Saving...' : 'Save'}
            </Button>
            <Button onClick={() => setView('list')} $variant="secondary">Cancel</Button>
        </ActionButtons>
        {!isEdit && <p style={{fontSize:'0.9rem', color:'#666', marginTop:'1rem'}}>You can add images after creating the post.</p>}
      </CardContent>
    </Card>
  );

  return (
    <Container>
      <h2>Manage News</h2>
      {msg && <div style={{color:'green', marginBottom:'1rem'}}>{msg}</div>}
      
      {view === 'list' && renderList()}
      {view === 'editHeader' && renderEditHeader()}
      {view === 'createPost' && renderPostForm(false)}
      {view === 'editPost' && renderPostForm(true)}
    </Container>
  );
};

export default NewsAdmin;
